import React, { useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { ChevronRight, ChevronLeft, Check, Loader2, Mail, Phone, Building2, User, GripVertical, ChevronDown, ChevronUp, Info, Clock, Users, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== AUDIT DATA ====================
const AUDIT_QUESTIONS = {
  part1: {
    title: "Structure Actuelle",
    subtitle: "Comprenons votre situation",
    questions: [
      {
        id: "status_professionnel",
        label: "Quel est votre statut professionnel ?",
        type: "radio_with_other",
        options: [
          "Infirmière injectrice indépendante",
          "Médecin esthétique indépendant",
          "Centre avec 2-5 praticiens",
          "Clinique multi-praticiens"
        ],
        otherLabel: "Autre (précisez)"
      },
      {
        id: "annees_experience",
        label: "Depuis combien de temps exercez-vous en esthétique ?",
        type: "radio",
        options: ["Moins de 2 ans", "2-5 ans", "5-10 ans", "Plus de 10 ans"]
      },
      {
        id: "equipe",
        label: "Travaillez-vous seul(e) ou avec une équipe ?",
        type: "radio",
        options: ["Seul(e)", "Avec une assistante", "Avec une équipe de 2-3 personnes", "Équipe de plus de 3 personnes"]
      },
      {
        id: "jours_consultation",
        label: "Combien de jours par semaine consultez-vous ?",
        type: "radio",
        options: ["1-2 jours", "3-4 jours", "5 jours", "Plus de 5 jours"]
      },
      {
        id: "objectif_principal",
        label: "Quels sont vos objectifs pour les 12 prochains mois ?",
        type: "checkbox",
        options: [
          "Remplir davantage l'agenda",
          "Attirer une clientèle plus premium",
          "Stabiliser mes revenus",
          "Réduire ma charge mentale",
          "Développer une nouvelle offre",
          "Améliorer ma visibilité en ligne"
        ]
      }
    ]
  },
  part2: {
    title: "Acquisition & Visibilité",
    subtitle: "Analysons vos sources de patients",
    questions: [
      {
        id: "source_patients",
        label: "D'où viennent principalement vos nouveaux patients ?",
        type: "checkbox",
        options: ["Bouche-à-oreille", "Instagram", "Google / SEO", "Publicité payante (Meta, Google Ads)", "Doctolib / Plateformes", "Partenariats locaux"]
      },
      {
        id: "nouveaux_patients_mois",
        label: "Combien de nouveaux patients obtenez-vous en moyenne par mois ?",
        type: "radio",
        options: ["Moins de 5", "5-10", "10-20", "20-30", "Plus de 30"]
      },
      {
        id: "systeme_transformation",
        label: "Avez-vous un système structuré pour transformer les demandes en rendez-vous ?",
        type: "radio",
        options: ["Oui, automatisé", "Oui, manuel", "Non, pas vraiment"]
      },
      {
        id: "demandes_non_relancees",
        label: "Combien de demandes entrantes ne sont pas relancées faute de temps ?",
        type: "radio",
        options: ["Aucune", "Quelques-unes", "Beaucoup", "Je ne sais pas"]
      },
      {
        id: "utilise_crm",
        label: "Disposez-vous d'un CRM pour suivre vos prospects ?",
        type: "radio",
        options: ["Oui", "Non"]
      },
      {
        id: "publicite_payante",
        label: "Avez-vous déjà investi dans de la publicité payante ?",
        type: "radio",
        options: ["Oui, avec de bons résultats", "Oui, résultats mitigés", "Oui, sans résultat", "Non, jamais"]
      },
      {
        id: "previsibilite_acquisition",
        label: "Sur une échelle de 1 à 10, à quel point votre acquisition est-elle prévisible ?",
        type: "slider",
        min: 1,
        max: 10
      }
    ]
  },
  part3: {
    title: "Gestion du Temps",
    subtitle: "Identifions les tâches chronophages",
    questions: [
      {
        id: "heures_messages",
        label: "Combien d'heures par semaine passez-vous à répondre aux messages (Instagram/WhatsApp) ?",
        type: "radio",
        options: ["Moins de 2h", "2-5h", "5-10h", "Plus de 10h"]
      },
      {
        id: "heures_rdv",
        label: "Combien d'heures par semaine passez-vous à confirmer les rendez-vous ?",
        type: "radio",
        options: ["Moins de 1h", "1-3h", "3-5h", "Plus de 5h"]
      },
      {
        id: "heures_relances",
        label: "Combien d'heures par semaine passez-vous à relancer les prospects hésitants ?",
        type: "radio",
        options: ["Moins de 1h", "1-3h", "3-5h", "Plus de 5h"]
      },
      {
        id: "heures_avis",
        label: "Combien d'heures par semaine passez-vous à gérer les avis Google ?",
        type: "radio",
        options: ["Moins de 1h", "1-2h", "2-4h", "Plus de 4h"]
      },
      {
        id: "heures_reseaux",
        label: "Combien d'heures par semaine passez-vous à publier sur les réseaux sociaux ?",
        type: "radio",
        options: ["Moins de 2h", "2-5h", "5-10h", "Plus de 10h"]
      },
      {
        id: "tache_prioritaire",
        label: "Classez ces tâches de la plus prioritaire à déléguer à la moins prioritaire :",
        type: "ranking",
        options: ["Gestion des messages", "Prise de rendez-vous", "Relances automatiques", "Création de contenu", "Suivi post-traitement", "Administration"]
      },
      {
        id: "tache_epuisante",
        label: "Quelle tâche vous épuise le plus aujourd'hui ?",
        type: "text"
      },
      {
        id: "automatisation_envisagee",
        label: "Avez-vous déjà envisagé d'automatiser certaines étapes ?",
        type: "text"
      }
    ]
  },
  part4: {
    title: "Conversion & Rentabilité",
    subtitle: "Mesurons votre performance commerciale",
    questions: [
      {
        id: "panier_moyen",
        label: "Quel est votre panier moyen par patient ?",
        type: "radio",
        options: ["Moins de 200€", "200-400€", "400-700€", "700-1000€", "Plus de 1000€"]
      },
      {
        id: "patients_reviennent",
        label: "Combien de patients reviennent dans l'année ?",
        type: "radio",
        options: ["Moins de 20%", "20-40%", "40-60%", "60-80%", "Plus de 80%"]
      },
      {
        id: "systeme_reactivation",
        label: "Avez-vous un système de suivi pour réactiver vos anciens patients ?",
        type: "radio",
        options: ["Oui", "Non"]
      },
      {
        id: "impact_nouveaux_patients",
        label: "Si vous gagniez 3 à 5 nouveaux patients qualifiés par mois, qu'est-ce que cela changerait ?",
        type: "text"
      },
      {
        id: "cout_acquisition_connu",
        label: "Savez-vous précisément combien vous coûte l'acquisition d'un nouveau patient ?",
        type: "radio",
        options: ["Oui, précisément", "Approximativement", "Non, pas du tout"]
      }
    ]
  },
  part5: {
    title: "Positionnement",
    subtitle: "Évaluons votre différenciation",
    questions: [
      {
        id: "differenciation_locale",
        label: "Comment vous différenciez-vous localement ?",
        type: "text"
      },
      {
        id: "dependance_plateforme",
        label: "Êtes-vous dépendant(e) d'une seule plateforme (Instagram par exemple) ?",
        type: "radio",
        options: ["Oui", "Partiellement", "Non"]
      },
      {
        id: "impact_blocage_compte",
        label: "Si votre principal canal d'acquisition était bloqué demain, que se passerait-il ?",
        type: "text"
      },
      {
        id: "competitivite_2026",
        label: "Pensez-vous que votre système actuel vous permettra de rester compétitif(ve) en 2026 ?",
        type: "radio",
        options: ["Oui", "Peut-être", "Non", "Je ne sais pas"]
      }
    ]
  },
  part6: {
    title: "Projection Stratégique",
    subtitle: "Imaginons votre futur",
    questions: [
      {
        id: "agenda_rempli_action",
        label: "Si votre agenda était rempli 3 mois à l'avance, que feriez-vous différemment ?",
        type: "text"
      },
      {
        id: "temps_libere_investissement",
        label: "Si vous pouviez éliminer 5 à 10 heures de tâches répétitives par semaine, où investiriez-vous ce temps ?",
        type: "text"
      },
      {
        id: "ouverture_systeme_structure",
        label: "Êtes-vous ouvert(e) à mettre en place un système structuré pour sécuriser votre croissance ?",
        type: "radio",
        options: ["Oui, absolument", "Oui, si cela fait sens", "À voir selon les conditions", "Non"]
      }
    ]
  }
};

// ==================== COMPONENTS ====================

const ProgressBar = ({ current, total }) => {
  const percentage = ((current + 1) / total) * 100;
  return (
    <div className="w-full max-w-2xl mx-auto mb-12">
      <div className="flex justify-between text-sm mb-3">
        <span className="text-[#A1A1AA] uppercase tracking-wider text-xs">Progression</span>
        <span className="text-white font-semibold">{current + 1} / {total}</span>
      </div>
      <div className="progress-bar">
        <div className="progress-bar-fill" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const RadioOption = ({ option, selected, onSelect }) => (
  <div
    data-testid={`radio-option-${option.replace(/\s+/g, '-').toLowerCase()}`}
    className={`radio-card ${selected ? 'selected' : ''}`}
    onClick={onSelect}
  >
    <div className="flex items-center gap-4">
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? 'border-white bg-white' : 'border-[#52525B]'}`}>
        {selected && <Check className="w-3 h-3 text-black" />}
      </div>
      <span className="text-white">{option}</span>
    </div>
  </div>
);

// Radio with "Autre" option
const RadioWithOther = ({ options, otherLabel, value, onChange }) => {
  const isOtherSelected = value && !options.includes(value) && value.startsWith('Autre:');
  const otherValue = isOtherSelected ? value.replace('Autre:', '').trim() : '';
  
  return (
    <div className="grid gap-3">
      {options.map((option) => (
        <RadioOption
          key={option}
          option={option}
          selected={value === option}
          onSelect={() => onChange(option)}
        />
      ))}
      <div
        data-testid="radio-option-autre"
        className={`radio-card ${isOtherSelected ? 'selected' : ''}`}
        onClick={() => onChange('Autre:')}
      >
        <div className="flex items-center gap-4">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isOtherSelected ? 'border-white bg-white' : 'border-[#52525B]'}`}>
            {isOtherSelected && <Check className="w-3 h-3 text-black" />}
          </div>
          <span className="text-white">{otherLabel}</span>
        </div>
      </div>
      {isOtherSelected && (
        <div className="ml-9 mt-2">
          <input
            data-testid="other-input"
            type="text"
            placeholder="Précisez votre profession..."
            value={otherValue}
            onChange={(e) => onChange(`Autre: ${e.target.value}`)}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-lg py-3 px-4 text-white placeholder-[#52525B] focus:border-white outline-none transition-colors"
          />
        </div>
      )}
    </div>
  );
};

const CheckboxOption = ({ option, selected, onToggle }) => (
  <div
    data-testid={`checkbox-option-${option.replace(/\s+/g, '-').toLowerCase()}`}
    className={`radio-card ${selected ? 'selected' : ''}`}
    onClick={onToggle}
  >
    <div className="flex items-center gap-4">
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selected ? 'border-white bg-white' : 'border-[#52525B]'}`}>
        {selected && <Check className="w-3 h-3 text-black" />}
      </div>
      <span className="text-white">{option}</span>
    </div>
  </div>
);

const SliderInput = ({ value, onChange, min, max }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <span className="text-[#52525B] text-sm">{min}</span>
      <span className="text-white text-4xl font-bold">{value}</span>
      <span className="text-[#52525B] text-sm">{max}</span>
    </div>
    <input
      data-testid="slider-input"
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-2 bg-[#121212] rounded-lg appearance-none cursor-pointer accent-white"
    />
  </div>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <textarea
    data-testid="text-input"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={4}
    className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 text-white placeholder-[#52525B] focus:border-white outline-none resize-none transition-colors"
  />
);

// Ranking Component with Drag & Drop
const RankingInput = ({ options, value, onChange }) => {
  const [items, setItems] = useState(value && value.length > 0 ? value : options);
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    
    setDraggedIndex(index);
    setItems(newItems);
    onChange(newItems);
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  
  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setItems(newItems);
    onChange(newItems);
  };
  
  return (
    <div className="space-y-2">
      <p className="text-[#A1A1AA] text-sm mb-4">Glissez-déposez pour réorganiser ou utilisez les flèches</p>
      {items.map((item, index) => (
        <div
          key={item}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-4 p-4 bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-xl cursor-move transition-all ${draggedIndex === index ? 'opacity-50 scale-95' : 'hover:border-[rgba(255,255,255,0.2)]'}`}
        >
          <div className="flex items-center gap-3 flex-1">
            <GripVertical className="w-5 h-5 text-[#52525B]" />
            <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-black flex items-center justify-center font-bold text-sm">
              {index + 1}
            </span>
            <span className="text-white">{item}</span>
          </div>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => moveItem(index, -1)}
              disabled={index === 0}
              className={`p-1 rounded ${index === 0 ? 'text-[#52525B]' : 'text-white hover:bg-white/10'}`}
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => moveItem(index, 1)}
              disabled={index === items.length - 1}
              className={`p-1 rounded ${index === items.length - 1 ? 'text-[#52525B]' : 'text-white hover:bg-white/10'}`}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const QuestionRenderer = ({ question, value, onChange }) => {
  switch (question.type) {
    case 'radio':
      return (
        <div className="grid gap-3">
          {question.options.map((option) => (
            <RadioOption
              key={option}
              option={option}
              selected={value === option}
              onSelect={() => onChange(option)}
            />
          ))}
        </div>
      );
    case 'radio_with_other':
      return (
        <RadioWithOther
          options={question.options}
          otherLabel={question.otherLabel}
          value={value}
          onChange={onChange}
        />
      );
    case 'checkbox':
      return (
        <div className="grid gap-3">
          {question.options.map((option) => (
            <CheckboxOption
              key={option}
              option={option}
              selected={Array.isArray(value) && value.includes(option)}
              onToggle={() => {
                const current = Array.isArray(value) ? value : [];
                if (current.includes(option)) {
                  onChange(current.filter(v => v !== option));
                } else {
                  onChange([...current, option]);
                }
              }}
            />
          ))}
        </div>
      );
    case 'slider':
      return (
        <SliderInput
          value={value || question.min}
          onChange={onChange}
          min={question.min}
          max={question.max}
        />
      );
    case 'text':
      return (
        <TextInput
          value={value || ''}
          onChange={onChange}
          placeholder="Votre réponse..."
        />
      );
    case 'ranking':
      return (
        <RankingInput
          options={question.options}
          value={value}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
};

// ==================== PAGES ====================

const LandingPage = ({ onStart }) => (
  <div className="min-h-screen flex flex-col lg:flex-row">
    <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 xl:px-24 py-16">
      <div className="max-w-xl animate-fade-in-up">
        <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-xs mb-6">Spécialiste Médecine Esthétique</p>
        <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight mb-6">
          Praticiens en médecine esthétique : <span className="font-accent text-[#D4AF37]">remplissez votre agenda</span> sans y passer vos soirées
        </h1>
        <p className="text-[#A1A1AA] text-lg lg:text-xl leading-relaxed mb-6">
          Que vous soyez infirmière injectrice, médecin esthétique ou que vous gériez un centre, cet audit gratuit révèle en 5 minutes les failles de votre acquisition et comment les transformer en patients qualifiés.
        </p>
        <div className="flex items-center gap-2 mb-8">
          <span className="text-[#EF4444] text-sm font-semibold">⚡</span>
          <span className="text-[#A1A1AA] text-sm">Nous accompagnons seulement <span className="text-white font-semibold">5 nouveaux praticiens par mois</span></span>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            data-testid="start-audit-btn"
            onClick={onStart}
            className="btn-primary flex items-center justify-center gap-3"
          >
            Faire mon audit gratuit
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-12 grid grid-cols-3 gap-6">
          <div>
            <p className="text-3xl font-bold text-white">150+</p>
            <p className="text-[#52525B] text-sm">Praticiens accompagnés</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-white">+40%</p>
            <p className="text-[#52525B] text-sm">Nouveaux patients en moyenne</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-white">10h</p>
            <p className="text-[#52525B] text-sm">Récupérées par semaine</p>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-[rgba(255,255,255,0.1)]">
          <p className="text-[#52525B] text-xs uppercase tracking-wider mb-4">Ils nous font confiance</p>
          <div className="flex flex-wrap gap-3">
            <span className="px-3 py-1.5 bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-full text-[#A1A1AA] text-xs">Infirmières injectrices</span>
            <span className="px-3 py-1.5 bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-full text-[#A1A1AA] text-xs">Médecins esthétiques</span>
            <span className="px-3 py-1.5 bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-full text-[#A1A1AA] text-xs">Centres esthétiques</span>
            <span className="px-3 py-1.5 bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-full text-[#A1A1AA] text-xs">Cliniques</span>
          </div>
        </div>
      </div>
    </div>
    
    <div className="flex-1 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-transparent to-transparent z-10" />
      <img
        src="https://images.unsplash.com/photo-1752213355644-64b6900e7841?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsYWNrJTIwc2lsayUyMHRleHR1cmUlMjBsdXh1cnklMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDkwNTY5OXww&ixlib=rb-4.1.0&q=85"
        alt="Blackcore"
        className="w-full h-full object-cover opacity-60"
      />
      <div className="absolute top-8 right-8 z-20">
        <h2 className="text-2xl font-extrabold tracking-tight">BLACKCORE</h2>
      </div>
    </div>
  </div>
);

const AuditForm = ({ onComplete }) => {
  const [currentPart, setCurrentPart] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({
    part1: { objectif_principal: [] },
    part2: { source_patients: [], previsibilite_acquisition: 5 },
    part3: { tache_prioritaire: ["Gestion des messages", "Prise de rendez-vous", "Relances automatiques", "Création de contenu", "Suivi post-traitement", "Administration"] },
    part4: {},
    part5: {},
    part6: {}
  });
  
  const parts = Object.keys(AUDIT_QUESTIONS);
  const currentPartKey = parts[currentPart];
  const currentPartData = AUDIT_QUESTIONS[currentPartKey];
  const currentQuestionData = currentPartData.questions[currentQuestion];
  
  const totalQuestions = parts.reduce((acc, part) => acc + AUDIT_QUESTIONS[part].questions.length, 0);
  const answeredQuestions = parts.slice(0, currentPart).reduce((acc, part) => acc + AUDIT_QUESTIONS[part].questions.length, 0) + currentQuestion;
  
  const handleAnswer = (value) => {
    setAnswers(prev => ({
      ...prev,
      [currentPartKey]: {
        ...prev[currentPartKey],
        [currentQuestionData.id]: value
      }
    }));
  };
  
  const canProceed = () => {
    const value = answers[currentPartKey][currentQuestionData.id];
    if (currentQuestionData.type === 'checkbox') {
      return Array.isArray(value) && value.length > 0;
    }
    if (currentQuestionData.type === 'slider' || currentQuestionData.type === 'ranking') {
      return true;
    }
    return value !== undefined && value !== '';
  };
  
  const handleNext = () => {
    if (currentQuestion < currentPartData.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (currentPart < parts.length - 1) {
      setCurrentPart(prev => prev + 1);
      setCurrentQuestion(0);
    } else {
      onComplete(answers);
    }
  };
  
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (currentPart > 0) {
      setCurrentPart(prev => prev - 1);
      setCurrentQuestion(AUDIT_QUESTIONS[parts[currentPart - 1]].questions.length - 1);
    }
  };
  
  const isFirst = currentPart === 0 && currentQuestion === 0;
  const isLast = currentPart === parts.length - 1 && currentQuestion === currentPartData.questions.length - 1;
  
  return (
    <div className="min-h-screen flex flex-col px-6 py-12">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-xl font-extrabold tracking-tight">BLACKCORE</h2>
        <span className="text-[#52525B] text-sm uppercase tracking-wider">Audit</span>
      </div>
      
      <ProgressBar current={answeredQuestions} total={totalQuestions} />
      
      <div className="flex-1 flex flex-col items-center justify-center max-w-2xl mx-auto w-full">
        <div className="w-full animate-fade-in-up" key={`${currentPart}-${currentQuestion}`}>
          <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs mb-2">
            Partie {currentPart + 1} — {currentPartData.title}
          </p>
          <p className="text-[#52525B] text-sm mb-8">{currentPartData.subtitle}</p>
          
          <h3 className="text-2xl lg:text-3xl font-bold mb-8 leading-tight">
            {currentQuestionData.label}
          </h3>
          
          <div className="mb-12">
            <QuestionRenderer
              question={currentQuestionData}
              value={answers[currentPartKey][currentQuestionData.id]}
              onChange={handleAnswer}
            />
          </div>
        </div>
        
        <div className="flex gap-4 w-full max-w-md">
          {!isFirst && (
            <button
              data-testid="back-btn"
              onClick={handleBack}
              className="btn-secondary flex items-center justify-center gap-2 flex-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </button>
          )}
          <button
            data-testid="next-btn"
            onClick={handleNext}
            disabled={!canProceed()}
            className="btn-primary flex items-center justify-center gap-2 flex-1"
          >
            {isLast ? 'Finaliser' : 'Continuer'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactForm = ({ onSubmit, isLoading }) => {
  const [contact, setContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: ''
  });
  
  const isValid = contact.first_name && contact.last_name && contact.email && contact.phone && contact.company_name;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) {
      onSubmit(contact);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-12">
          <h2 className="text-xl font-extrabold tracking-tight mb-8">BLACKCORE</h2>
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-xs mb-4">Dernière étape</p>
          <h1 className="text-3xl lg:text-4xl font-bold mb-4">
            Recevez votre rapport personnalisé
          </h1>
          <p className="text-[#A1A1AA]">
            Vos coordonnées nous permettront de vous envoyer votre rapport détaillé sous 24h.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-[#52525B] text-xs uppercase tracking-wider mb-2">Prénom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input
                  data-testid="first-name-input"
                  type="text"
                  placeholder="Prénom"
                  value={contact.first_name}
                  onChange={(e) => setContact(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-lg py-3 pl-10 pr-4 text-white placeholder-[#52525B] focus:border-white outline-none transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#52525B] text-xs uppercase tracking-wider mb-2">Nom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <input
                  data-testid="last-name-input"
                  type="text"
                  placeholder="Nom"
                  value={contact.last_name}
                  onChange={(e) => setContact(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-lg py-3 pl-10 pr-4 text-white placeholder-[#52525B] focus:border-white outline-none transition-colors"
                />
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-[#52525B] text-xs uppercase tracking-wider mb-2">Email professionnel</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
              <input
                data-testid="email-input"
                type="email"
                placeholder="votre@email.com"
                value={contact.email}
                onChange={(e) => setContact(prev => ({ ...prev, email: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-lg py-3 pl-10 pr-4 text-white placeholder-[#52525B] focus:border-white outline-none transition-colors"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-[#52525B] text-xs uppercase tracking-wider mb-2">Téléphone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
              <input
                data-testid="phone-input"
                type="tel"
                placeholder="06 XX XX XX XX"
                value={contact.phone}
                onChange={(e) => setContact(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-lg py-3 pl-10 pr-4 text-white placeholder-[#52525B] focus:border-white outline-none transition-colors"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-[#52525B] text-xs uppercase tracking-wider mb-2">Nom du cabinet / entreprise</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
              <input
                data-testid="company-input"
                type="text"
                placeholder="Nom de l'entreprise"
                value={contact.company_name}
                onChange={(e) => setContact(prev => ({ ...prev, company_name: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-lg py-3 pl-10 pr-4 text-white placeholder-[#52525B] focus:border-white outline-none transition-colors"
              />
            </div>
          </div>
          
          <button
            data-testid="submit-btn"
            type="submit"
            disabled={!isValid || isLoading}
            className="btn-primary w-full flex items-center justify-center gap-3 mt-8"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                Obtenir mon rapport
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
        
        <p className="text-[#52525B] text-xs text-center mt-6">
          Vos données sont confidentielles et ne seront jamais partagées.
        </p>
      </div>
    </div>
  );
};

const ScoreCircle = ({ score }) => {
  const circumference = 2 * Math.PI * 90;
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className="score-circle">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <circle cx="100" cy="100" r="90" className="score-circle-bg" />
        <circle
          cx="100"
          cy="100"
          r="90"
          className="score-circle-progress"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-extrabold">{score}</span>
        <span className="text-[#52525B] text-sm">/100</span>
      </div>
    </div>
  );
};

const GlossaireTerm = ({ terme, definition }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-[rgba(255,255,255,0.1)] last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left hover:text-[#D4AF37] transition-colors"
      >
        <span className="font-semibold text-white">{terme}</span>
        <Info className={`w-4 h-4 text-[#52525B] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <p className="pb-4 text-[#A1A1AA] text-sm leading-relaxed animate-fade-in-up">
          {definition}
        </p>
      )}
    </div>
  );
};

const ResultsPage = ({ result }) => {
  const [activeTab, setActiveTab] = useState('analyse');
  
  const scoreLabels = {
    acquisition: { label: 'Acquisition', max: 30 },
    conversion: { label: 'Conversion', max: 20 },
    previsibilite: { label: 'Prévisibilité', max: 20 },
    automatisation: { label: 'Automatisation', max: 20 },
    dependance_risque: { label: 'Risque', max: 10 }
  };
  
  const tabs = [
    { id: 'analyse', label: 'Analyse' },
    { id: 'plan', label: 'Plan d\'Action' },
    { id: 'glossaire', label: 'Glossaire' }
  ];
  
  return (
    <div className="min-h-screen px-6 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-xl font-extrabold tracking-tight mb-8">BLACKCORE</h2>
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-xs mb-4">Résultats de votre audit</p>
          <h1 className="text-3xl lg:text-4xl font-bold">
            Votre diagnostic est prêt
          </h1>
        </div>
        
        {/* Score Circle */}
        <div className="flex justify-center mb-12 animate-fade-in-up stagger-1" style={{opacity: 0}}>
          <ScoreCircle score={result.score_global} />
        </div>
        
        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8 animate-fade-in-up stagger-2" style={{opacity: 0}}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-full font-semibold text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'bg-[#0A0A0A] text-[#A1A1AA] border border-[rgba(255,255,255,0.1)] hover:border-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        {activeTab === 'analyse' && (
          <div className="space-y-8 animate-fade-in-up">
            {/* Score Breakdown */}
            <div className="glass-card p-8" data-testid="scores-breakdown">
              <h3 className="text-lg font-bold mb-6 text-[#D4AF37] uppercase tracking-wider text-sm">Répartition du score</h3>
              <div className="space-y-4">
                {Object.entries(result.scores).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-[#A1A1AA]">{scoreLabels[key]?.label || key}</span>
                    <div className="flex items-center gap-4">
                      <div className="w-32 h-2 bg-[#121212] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-white rounded-full transition-all duration-1000"
                          style={{ width: `${(value / scoreLabels[key]?.max) * 100}%` }}
                        />
                      </div>
                      <span className="text-white font-semibold w-12 text-right">
                        {value}/{scoreLabels[key]?.max}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Synthèse */}
            <div className="glass-card p-8" data-testid="synthese">
              <h3 className="text-lg font-bold mb-4 text-[#D4AF37] uppercase tracking-wider text-sm">Notre analyse</h3>
              <p className="text-white leading-relaxed text-lg">{result.rapport_synthese}</p>
            </div>
            
            {/* Grid: Fragilités & Leviers */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card p-8" data-testid="fragilites">
                <h3 className="text-lg font-bold mb-6 text-[#EF4444] uppercase tracking-wider text-sm">Ce qui vous coûte aujourd'hui</h3>
                <div className="space-y-6">
                  {result.fragilites.map((item, idx) => (
                    <div key={idx} className="border-l-2 border-[#EF4444] pl-4">
                      {typeof item === 'object' ? (
                        <>
                          <p className="text-white font-semibold mb-2">{item.titre}</p>
                          <p className="text-[#A1A1AA] text-sm mb-2">{item.description}</p>
                          <p className="text-[#EF4444] text-xs font-semibold">{item.cout_cache}</p>
                        </>
                      ) : (
                        <p className="text-[#A1A1AA]">{item}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="glass-card p-8" data-testid="leviers">
                <h3 className="text-lg font-bold mb-6 text-[#10B981] uppercase tracking-wider text-sm">Vos opportunités</h3>
                <div className="space-y-6">
                  {result.leviers.map((item, idx) => (
                    <div key={idx} className="border-l-2 border-[#10B981] pl-4">
                      {typeof item === 'object' ? (
                        <>
                          <p className="text-white font-semibold mb-2">{item.titre}</p>
                          <p className="text-[#A1A1AA] text-sm mb-2">{item.description}</p>
                          <p className="text-[#10B981] text-xs font-semibold">{item.gain_potentiel}</p>
                        </>
                      ) : (
                        <p className="text-[#A1A1AA]">{item}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Projection */}
            <div className="glass-card p-8" data-testid="projection">
              <h3 className="text-lg font-bold mb-6 text-[#D4AF37] uppercase tracking-wider text-sm">Votre potentiel dans 6 mois</h3>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-[#121212] rounded-xl p-6 text-center">
                  <Clock className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-2">Temps récupéré</p>
                  <p className="text-2xl font-bold text-white">{result.projection?.temps_recupere_semaine || 'N/A'}</p>
                  <p className="text-[#52525B] text-xs">par semaine</p>
                </div>
                <div className="bg-[#121212] rounded-xl p-6 text-center">
                  <Users className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-2">Nouveaux patients</p>
                  <p className="text-2xl font-bold text-white">{result.projection?.nouveaux_patients_mois || 'N/A'}</p>
                  <p className="text-[#52525B] text-xs">par mois</p>
                </div>
                <div className="bg-[#121212] rounded-xl p-6 text-center">
                  <TrendingUp className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-2">CA supplémentaire</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">{result.projection?.ca_supplementaire_annuel || 'N/A'}</p>
                  <p className="text-[#52525B] text-xs">par an</p>
                </div>
              </div>
              {result.projection?.message_projection && (
                <p className="text-[#A1A1AA] text-center italic leading-relaxed">{result.projection.message_projection}</p>
              )}
            </div>
            
            {/* Conclusion */}
            <div className="glass-card p-8" data-testid="conclusion">
              <p className="text-white leading-relaxed text-lg text-center">{result.conclusion}</p>
            </div>
          </div>
        )}
        
        {activeTab === 'plan' && (
          <div className="space-y-6 animate-fade-in-up">
            <div className="glass-card p-8">
              <h3 className="text-lg font-bold mb-2 text-[#D4AF37] uppercase tracking-wider text-sm">Plan d'action recommandé</h3>
              <p className="text-[#A1A1AA] mb-8">Voici les étapes concrètes pour transformer votre activité</p>
              
              <div className="space-y-6">
                {result.plan_action?.map((step, idx) => (
                  <div key={idx} className="bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] rounded-xl p-6 hover:border-[#D4AF37] transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-black flex items-center justify-center font-bold flex-shrink-0">
                        {step.etape || idx + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-bold text-lg mb-2">{step.titre}</h4>
                        <p className="text-[#A1A1AA] leading-relaxed mb-4">{step.description}</p>
                        
                        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                          <div>
                            <p className="text-[#52525B] text-xs uppercase mb-1">Temps gagné</p>
                            <p className="text-white font-semibold text-sm">{step.impact_temps}</p>
                          </div>
                          <div>
                            <p className="text-[#52525B] text-xs uppercase mb-1">Nouveaux patients</p>
                            <p className="text-white font-semibold text-sm">{step.impact_clients}</p>
                          </div>
                          <div>
                            <p className="text-[#52525B] text-xs uppercase mb-1">CA potentiel</p>
                            <p className="text-[#D4AF37] font-semibold text-sm">{step.impact_ca}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'glossaire' && (
          <div className="animate-fade-in-up">
            <div className="glass-card p-8">
              <h3 className="text-lg font-bold mb-2 text-[#D4AF37] uppercase tracking-wider text-sm">Glossaire</h3>
              <p className="text-[#A1A1AA] mb-8">Les termes techniques expliqués simplement</p>
              
              <div className="divide-y divide-[rgba(255,255,255,0.1)]">
                {result.glossaire?.map((item, idx) => (
                  <GlossaireTerm key={idx} terme={item.terme} definition={item.definition} />
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Témoignage Vidéo */}
        <div className="mt-12 animate-fade-in-up">
          <div className="glass-card p-8">
            <div className="text-center mb-8">
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs mb-3">Ils nous ont fait confiance</p>
              <h3 className="text-2xl font-bold text-white">Découvrez les témoignages de nos praticiens</h3>
            </div>
            <div className="relative w-full max-w-3xl mx-auto aspect-video rounded-xl overflow-hidden border border-[rgba(255,255,255,0.1)]">
              <iframe
                src="https://www.youtube.com/embed/r7FkLyBzvuM"
                title="Témoignage client Blackcore"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </div>
        
        {/* CTA Prise de RDV */}
        <div className="mt-12 animate-fade-in-up">
          <div className="glass-card p-10 text-center border-[#D4AF37] border">
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs mb-4">Prochaine étape</p>
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              Discutons de votre stratégie d'acquisition
            </h3>
            <p className="text-[#A1A1AA] mb-6 max-w-xl mx-auto">
              Réservez un appel stratégique de 30 minutes pour analyser ensemble vos résultats et définir un plan d'action personnalisé.
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="text-[#EF4444] text-sm">⚡</span>
              <span className="text-[#A1A1AA] text-sm">Places limitées : <span className="text-white font-semibold">5 praticiens accompagnés par mois</span></span>
            </div>
            <a
              href="https://api.leadconnectorhq.com/widget/booking/Cmd3ItSVjYXJlkqTZF8o"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-booking-btn"
              className="btn-primary inline-flex items-center justify-center gap-3 text-lg px-10 py-5"
            >
              Réserver mon appel stratégique
              <ChevronRight className="w-5 h-5" />
            </a>
            <p className="text-[#52525B] text-xs mt-4">Gratuit et sans engagement</p>
          </div>
        </div>
        
        {/* Email Notice */}
        <div className="text-center mt-12 animate-fade-in-up">
          <p className="text-[#52525B] mb-4">Un rapport détaillé vous a été envoyé par email.</p>
          <p className="text-[#A1A1AA]">
            Nous vous contacterons dans les <span className="text-white font-semibold">24 heures</span> pour discuter de vos résultats.
          </p>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-16 pt-8 border-t border-[rgba(255,255,255,0.1)]">
          <p className="text-[#52525B] text-sm">© 2026 Blackcore - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================

function App() {
  const [step, setStep] = useState('landing');
  const [auditAnswers, setAuditAnswers] = useState(null);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleStartAudit = () => {
    setStep('audit');
  };
  
  const handleAuditComplete = (answers) => {
    setAuditAnswers(answers);
    setStep('contact');
  };
  
  const handleContactSubmit = async (contact) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const payload = {
        contact,
        part1: auditAnswers.part1,
        part2: auditAnswers.part2,
        part3: auditAnswers.part3,
        part4: auditAnswers.part4,
        part5: auditAnswers.part5,
        part6: auditAnswers.part6
      };
      
      const response = await axios.post(`${API}/audit/submit`, payload);
      setResult(response.data);
      setStep('results');
    } catch (err) {
      console.error('Error submitting audit:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="App">
      {step === 'landing' && <LandingPage onStart={handleStartAudit} />}
      {step === 'audit' && <AuditForm onComplete={handleAuditComplete} />}
      {step === 'contact' && (
        <ContactForm onSubmit={handleContactSubmit} isLoading={isLoading} />
      )}
      {step === 'results' && result && <ResultsPage result={result} />}
      
      {error && (
        <div className="fixed bottom-4 right-4 bg-[#EF4444] text-white px-6 py-3 rounded-lg shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
}

export default App;
